import { useTheme } from "styled-components";

import type { HaNConnectionDesk } from "@lib/logic";
import { useConnectionReservationsWithUsers } from "@lib/store/src/ui/hooks/timeslots/useConnectionReservationsWithUsers";
import { dateToLocalDateString, dateToLocalDateTimeString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useUserProfilePics } from "@hooks";
import { toMapViewUser } from "@utils";

/** The floor's desks that connections have booked right now, with their avatars. */
export const useConnectionDesks = ({ floorId, now }: { floorId: string; now: Date }): HaNConnectionDesk[] => {
  const theme = useTheme();
  const nowKey = dateToLocalDateTimeString(now);

  const ongoingDeskReservations = useConnectionReservationsWithUsers(dateToLocalDateString(now)).flatMap(
    ({ reservation, user }) => {
      const { workspace } = reservation;

      if (!("deskId" in workspace) || workspace.floorId !== floorId) return [];
      if (nowKey < reservation.localStart || nowKey > reservation.localEnd) return [];

      return [{ deskId: workspace.deskId, user }];
    },
  );

  const profilePictures = useUserProfilePics(
    ongoingDeskReservations.map(({ user }) => ({ userId: user.id, imageHash: user.imageHash ?? "" })),
  );

  return ongoingDeskReservations.map(({ deskId, user }) => {
    return {
      deskId,
      connection: toMapViewUser(
        { email: user.email, initials: user.initials, imageUrl: profilePictures[user.id] },
        theme,
      ),
    };
  });
};
